import React, { useState, useRef, useEffect, memo } from 'react';
import { shape, func } from 'prop-types';
import { useDispatch } from 'react-redux';

import ToggleSwitch from 'components/ToggleSwitch';
import IconWithText from 'components/IconWithText';
import Image from 'components/Image';
import TxtpayForm from 'components/TxtpayForm';
import EmbedCode from 'components/EmbedCode';

import { ReactComponent as ChevronUp } from 'assets/icons/chevron-up.svg';
import { ReactComponent as ChevronDown } from 'assets/icons/chevron-down.svg';
import { ReactComponent as LinkIcon } from 'assets/icons/link.svg';
import { ReactComponent as CodeIcon } from 'assets/icons/code.svg';
import { ReactComponent as TxtPayIcon } from 'assets/icons/txtpay.svg';
import { ReactComponent as CloneIcon } from 'assets/icons/copy.svg';

import { productList as TEXT } from 'locales/en';
import { FIELD_NAMES } from 'pages/Product/schema';
import { useMediaQuery } from 'hooks';
import breakpoints from 'utils/breakpoints';
import { showSuccessMessage } from 'redux/slices/notification';

const LINK = 'link';
const TXTPAY = 'txtpay';
const EMBED_CODE = 'embedCode';

const ProductThumbnail = ({ product, onChangeOnSale, onClickClone }) => {
  const [expandedView, setExpandedView] = useState(null);
  const expandedRef = useRef(null);
  const dispatch = useDispatch();
  const { image, name, currency_symbol, price, id, on_sale, checkout_url } =
    product;
  const isLarge = useMediaQuery(breakpoints.lg);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (expandedView && expandedRef.current) {
      expandedRef.current.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [expandedView]);

  const onCopy = () => {
    navigator.clipboard.writeText(checkout_url);
    dispatch(
      showSuccessMessage({
        message: 'Link copied to clipboard!',
        timeout: 1000,
      })
    );
  };
  const onCopyProduct = () => {
    if (onClickClone) {
      const defaultValues = {
        [FIELD_NAMES.image]: '',
        [FIELD_NAMES.name]: name,
        [FIELD_NAMES.price]: price,
        [FIELD_NAMES.isVatExclusive]: !!product.is_vat_exclusive,
        [FIELD_NAMES.vatRate]: product.vat_rate,
        [FIELD_NAMES.description]: product.description,
        [FIELD_NAMES.hasStockLimit]: !!product.has_stock_limit,
        [FIELD_NAMES.availableStock]: product.available_stock,
        [FIELD_NAMES.autoAccept]: product.auto_accept_orders,
        [FIELD_NAMES.isRequestingInformation]: false,
        [FIELD_NAMES.video_embed]: product.video_embed,
      };

      onClickClone(defaultValues);
    }
  };

  const onClickExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const onClickItem = (item) => {
    if (item === expandedView) {
      setExpandedView(null);
    } else {
      setExpandedView(item);
    }
  };

  return (
    <div
      id="product-thumbnail"
      className={`card lg:min-h-60 lg:h-auto lg:pt-8 lg:pb-4 lg:px-8 lg:relative transition-height ease-in-out ${
        isExpanded ? 'h-auto' : 'h-20'
      }`}
    >
      <div className="flex lg:w-full">
        <div className="w-28 h-14 lg:h-full overflow-hidden rounded-md">
          <Image src={image} alt={name} />
        </div>
        <div className="w-5/6 h-auto ml-2 lg:w-5/12 lg:h-40">
          <h1
            data-testid="name"
            className="text-sm lg:text-base font-bold text-javelin-gray-dark truncate"
          >
            {name}
          </h1>
          <p
            data-testid="price"
            className="text-sm lg:text-2xl lg:font-bold text-javelin-gray-dark mt-2"
          >
            {`${currency_symbol}${price}`}
          </p>
          {isLarge && (
            <ToggleSwitch
              value={on_sale}
              onChange={onChangeOnSale}
              className="mt-4"
            />
          )}
        </div>
        {isExpanded ? (
          <ChevronUp
            className="w-4 h-4 my-auto opacity-40 cursor-pointer lg:hidden"
            onClick={onClickExpand}
          />
        ) : (
          <ChevronDown
            className="w-4 h-4 my-auto opacity-40 cursor-pointer lg:hidden"
            onClick={onClickExpand}
          />
        )}
      </div>
      <hr className="horizontal-line lg:hidden" />
      <div className="w-full lg:w-9/12 lg:ml-auto lg:pl-2">
        <div className="flex justify-between items-center lg:mb-8">
          {!isLarge && (
            <ToggleSwitch value={on_sale} onChange={onChangeOnSale} />
          )}
          <span className="lg:mt-0 lg:absolute lg:top-9 lg:right-8">
            <IconWithText
              icon={CloneIcon}
              text={TEXT.clone}
              textOnLeft={!isExpanded}
              onClick={onCopyProduct}
              className="h-8 cursor-pointer hover:text-black"
              textClassName="ml-2"
            />
          </span>
        </div>
      </div>
      <hr className="horizontal-line lg:hidden" />
      <div className="w-full">
        <div className="items-center justify-between h-auto flex flex-col lg:flex-row">
          <div className="flex flex-col items-center lg:flex-col w-full lg:w-auto">
            <IconWithText
              icon={LinkIcon}
              text={TEXT.getLink}
              onClick={() => onClickItem(LINK)}
              testId="get-link"
              className="h-8 cursor-pointer"
              iconClassName={`h-4 ${
                expandedView === LINK
                  ? 'text-brand-primary'
                  : 'text-javelin-gray-light'
              }`}
              textClassName="ml-1"
            />
            {expandedView === LINK && !isLarge && (
              <div className="py-2 w-full lg:w-auto">
                <p className="text-opacity-40 text-black text-center text-xs mb-2">
                  {TEXT.linkInfo}
                </p>
                <div className="border flex w-full border-gray-200 px-2 rounded-md text-gray-400 justify-between items-center">
                  <span className="font-light text-sm">{checkout_url}</span>
                  <span
                    data-testid="copy-link"
                    className="bg-gray-100 text-sm cursor-pointer text-gray-400 rounded uppercase px-4 my-2 py-1 h-full float-right hover:bg-brand-primary hover:text-gray-100 select-none"
                    onClick={onCopy}
                  >
                    {TEXT.copy}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col items-center lg:flex-col w-full lg:w-auto">
            <IconWithText
              icon={CodeIcon}
              text={TEXT.getCode}
              className="h-8 cursor-pointer"
              iconClassName={`h-4 ${
                expandedView === EMBED_CODE
                  ? 'text-brand-primary'
                  : 'text-javelin-gray-light'
              }`}
              textClassName="ml-1"
              onClick={() => onClickItem(EMBED_CODE)}
              testId="get-embed-code"
            />
            {expandedView === EMBED_CODE && !isLarge && (
              <EmbedCode
                ref={expandedRef}
                id={id}
                className="w-full lg:w-auto"
                buttonText="Buy Now"
              />
            )}
          </div>
          <div className="flex flex-col items-center lg:flex-col w-full lg:w-auto">
            <IconWithText
              icon={TxtPayIcon}
              text={TEXT.share}
              className="h-8 cursor-pointer"
              iconClassName={`h-6 ${
                expandedView === TXTPAY
                  ? 'text-brand-primary'
                  : 'text-javelin-gray-light'
              }`}
              textClassName="ml-1"
              testId="txtpay-share"
              onClick={() => onClickItem(TXTPAY)}
            />
            {expandedView === TXTPAY && !isLarge && (
              <TxtpayForm
                ref={expandedRef}
                id={id}
                fundraiserName={name}
                checkoutUrl={checkout_url}
                className="w-full lg:w-auto"
              />
            )}
          </div>
        </div>
        {isLarge && (
          <>
            {expandedView === TXTPAY && (
              <TxtpayForm
                ref={expandedRef}
                id={id}
                fundraiserName={name}
                checkoutUrl={checkout_url}
                className="lg:py-4"
              />
            )}
            {expandedView === EMBED_CODE && (
              <EmbedCode
                ref={expandedRef}
                id={id}
                className="lg:py-4"
                buttonText="Buy Now"
              />
            )}
            {expandedView === LINK && (
              <div className="lg:py-4">
                <p className="text-opacity-40 text-black text-xs mb-4">
                  {TEXT.linkInfo}
                </p>
                <div className="border flex w-full border-gray-200 px-2 rounded-md text-gray-400 justify-between items-center">
                  <span className="font-light text-sm">{checkout_url}</span>
                  <span
                    data-testid="copy-link"
                    className="bg-gray-100 text-sm cursor-pointer text-gray-400 font-light rounded uppercase px-4 my-2 py-1 h-full float-right hover:bg-brand-primary hover:text-gray-100 transition-colors duration-300"
                    onClick={onCopy}
                  >
                    {TEXT.copy}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ProductThumbnail.propTypes = {
  product: shape({}),
  onClickClone: func,
  onChangeOnSale: func,
};
export default memo(ProductThumbnail);
